import AlertCircleIcon from "@redotech/redo-icons/alert-circle.svg";
import CheckCircleIcon from "@redotech/redo-icons/check-circle.svg";
import ClockIcon from "@redotech/redo-icons/clock.svg";
import { RedoAlert } from "@redotech/redo-web/arbiter-components/alert/redo-alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { Flex } from "@redotech/redo-web/flex";
import { memo, useContext } from "react";
import { useSetupGuideOptional } from "../../provider/use-setup-guide";
import type { TaskContextualModalProps } from "../../registry/types";
import { SENDING_NUMBER_TASK_ID } from "./constants";

const COPY = {
  complete: {
    alertColor: "success",
    Icon: CheckCircleIcon,
    title: "Your sending number is approved",
    description:
      "Concierge can now text shoppers from your approved sending number.",
  },
  pending: {
    alertColor: "warning",
    Icon: ClockIcon,
    title: "Your sending number is being reviewed",
    description:
      "There's nothing else you need to do right now. We'll keep checking in the background and Concierge will go live automatically once SMS is approved.",
    iMessageDescription:
      "Concierge can start conversations with iPhone shoppers through iMessage now. We'll keep checking in the background and it can reach the rest of your shoppers once SMS is approved.",
  },
  needs_attention: {
    alertColor: "error",
    Icon: AlertCircleIcon,
    title: "Your sending number needs attention",
    description:
      "We hit an issue while preparing the number Concierge uses for SMS.",
  },
  not_started: {
    alertColor: "gray",
    Icon: ClockIcon,
    title: "Sending number setup has not started yet",
    description:
      "Finish Concierge setup so we can prepare the number it uses to text shoppers.",
  },
} as const;

export const SendingNumberStatusModal = memo(function SendingNumberStatusModal({
  onClose,
}: TaskContextualModalProps) {
  const setupGuide = useSetupGuideOptional();
  const team = useContext(TeamContext);
  const asyncStatus = setupGuide?.asyncTaskStatuses[SENDING_NUMBER_TASK_ID];
  const intakeStatus =
    setupGuide?.activeProgress?.intakeAnswers.phoneProvisioningStatus;
  const hasIMessage = team?.settings?.shopperAgentAdmin?.allowIMessage === true;

  const variant =
    asyncStatus?.variant ??
    (asyncStatus?.complete
      ? "complete"
      : intakeStatus === "pending"
        ? "pending"
        : intakeStatus === "approved"
          ? "complete"
          : "not_started");
  const copy = COPY[variant];
  const description =
    variant === "needs_attention"
      ? (asyncStatus?.description ?? copy.description)
      : variant === "pending" && hasIMessage
        ? COPY.pending.iMessageDescription
        : copy.description;

  return (
    <Flex dir="column" gap="lg" w="full">
      <RedoAlert
        align="flex-start"
        color={copy.alertColor}
        description={description}
        fullWidth
        Icon={copy.Icon}
        title={copy.title}
      />

      <Flex justify="flex-end">
        <RedoButton
          hierarchy="primary"
          onClick={onClose}
          size="sm"
          text="Close"
        />
      </Flex>
    </Flex>
  );
});
